import { Component, OnInit, ViewChild } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute } from "@angular/router";
import { debounceTime, takeUntil } from "rxjs/operators";
import { AppService, RegionDTO } from "src/app/apps/app.service";
import { NotificationService } from "src/app/notifications/notification.service";
import { SessionService } from "src/app/session.service";
import { AreYouSureDialogComponent } from "src/app/shared/dialogs/are-you-sure-dialog/are-you-sure-dialog.component";
import { QuestionAnswerTextComponent } from "src/app/shared/dialogs/question-answer-text/question-answer-text.component";
import { UiService } from "src/app/ui/ui.service";
import { pageSizeOptions } from "src/app/shared/utils/pagination";
import { MatTable, MatTableDataSource } from "@angular/material/table";
import { MatSort } from "@angular/material/sort";
import { MatPaginator, PageEvent } from "@angular/material/paginator";
import { Subject } from "rxjs";
import { FileSaverService } from "ngx-filesaver";
import moment from "moment";
import { ApiService } from "src/app/api.service";
import { LocalStorageService } from "src/app/storage/local-storage.service";

@Component({
  selector: "app-regions",
  templateUrl: "./regions.component.html",
  styleUrls: ["./regions.component.scss"],
})
export class RegionsComponent implements OnInit {
  pageSizeOptions = pageSizeOptions;
  displayedColumns = [
    "name",
    "avg_tos",
    "total_visits",
    "unique_visits",
    "avg_visit_freq",
  ];
  filterForm!: FormGroup;
  availableRegions!: RegionDTO[];
  selectedRegions!: RegionDTO[];
  showCreatePalette = false;
  showCreatePaletteAtEnd = false;
  showSwapSelect = false;
  paletteForm!: FormGroup;
  comparisonGroup: any;
  availableComparisonGroups!: {
    id: number;
    name: string;
    regions: RegionDTO[];
  }[];
  regionsForm!: FormGroup;
  comparisonGroupsDTO: any;
  searchValue = new FormControl("");
  tileView = true;
  regionsDataSource!: MatTableDataSource<RegionDTO>;
  @ViewChild("regionsTable") regionsTable!: MatTable<RegionDTO[]>;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  private onComponentDestroy = new Subject();
  perPage: number = 20;

  constructor(
    private route: ActivatedRoute,
    private Dialog: MatDialog,
    private SessionService: SessionService,
    private AppService: AppService,
    private NotificationService: NotificationService,
    private uiService: UiService,
    private fileSaver: FileSaverService,
    private APIService: ApiService,
    private localStorageSvc: LocalStorageService,
  ) {}

  ngOnInit(): void {

    this.perPage = this.localStorageSvc.pageSize || 20;

    this.initAvailableRegions(this.route.snapshot.data.regions);

    this.comparisonGroupsDTO = this.route.snapshot.data.compareRegionGroups;

    this.initAvailableComparisonGroups();

    this.filterForm = new FormGroup({
      startDate: new FormControl(this.route.snapshot.data.start),
      endDate: new FormControl(this.route.snapshot.data.end),
    });

    this.paletteForm = new FormGroup({
      addPallate: new FormControl(),
      swapPallete: new FormControl(),
    });

    this.regionsForm = new FormGroup({
      comparisonGroup: new FormControl(),
    });

    this.regionsForm.controls.comparisonGroup.valueChanges
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((comparisonGroup) => {
        this.comparisonGroup = comparisonGroup;
      });

    this.searchValue.valueChanges
      .pipe(debounceTime(200))
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(() => {
        this.applyFilter();
      });

    this.filterForm.valueChanges
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((change) => {
        if (change.startDate < change.endDate) {
          this.filterForm.controls.endDate.value.setHours(23, 59, 59);

          this.uiService
            .waitForEvent(
              this.AppService.getRegions(
                this.SessionService.currentAccountValue!.accountID,
                change.startDate,
                this.filterForm.controls.endDate.value,
                false
              ),
            )
            .pipe(takeUntil(this.onComponentDestroy))
            .pipe(takeUntil(this.onComponentDestroy))
            .subscribe(
              (regions) => {
                this.initAvailableRegions(regions);

                let comparisonGroupID: number | undefined;
                if (this.comparisonGroup) {
                  comparisonGroupID = this.comparisonGroup.id;
                }
                this.initAvailableComparisonGroups();
                if (comparisonGroupID) {
                  const comparisonGroup = this.availableComparisonGroups.find(
                    (group: any) => group.id === comparisonGroupID,
                  );
                  this.regionsForm.controls.comparisonGroup.setValue(
                    comparisonGroup,
                  );
                }
                this.initTable();
                this.applyFilter();
              },
              (err) => {
                this.NotificationService.openErrorSnackBar(
                  "Error retrieving information",
                );
              },
            );
        }
      });
  }

  ngAfterViewInit() {
    this.initTable();
  }

  ngOnDestroy() {
    this.onComponentDestroy.next();
    this.onComponentDestroy.complete();
  }

  get listView() {
    return !this.tileView;
  }

  set listView(value: boolean) {
    this.tileView = !value;
  }

  get hasUnselectedRegions() {
    if (this.compareRegionMode) {
      return (
        this.availableRegions.length !== this.comparisonGroup.regions.length
      );
    }
    return this.availableRegions.length !== this.selectedRegions.length;
  }

  get compareRegionMode() {
    return !!this.comparisonGroup;
  }

  get unselectedRegions() {
    if (this.compareRegionMode) {
      return this.availableRegions.filter((availableRegion: any) => {
        return !this.comparisonGroup.regions.find(
          (comparisonGroupRegions: any) => {
            return (
              comparisonGroupRegions.id === availableRegion.id &&
              comparisonGroupRegions.type === availableRegion.type
            );
          },
        );
      });
    }
    return this.availableRegions.filter((availableRegion: any) => {
      return !this.selectedRegions.find((selectedRegion) => {
        return (
          selectedRegion.id === availableRegion.id &&
          selectedRegion.type === availableRegion.type
        );
      });
    });
  }

  get showCreatePalleteBtn() {
    return !this.showCreatePalette && !this.showCreatePaletteAtEnd;
  }

  initTable() {
    if (!this.regionsDataSource) {
      this.regionsDataSource = new MatTableDataSource();
    }
    this.regionsDataSource.data = this.selectedRegions;
    this.regionsDataSource.sort = this.sort;
    this.regionsDataSource.paginator = this.paginator;
  }

  initAvailableRegions(regions: any) {
    this.availableRegions = regions
      ? regions.map((region: any) => {
          return {
            ...region,
            typeName: region.type === "geofence" ? "Geofence" : "Beacon Region",
            avg_visit_freq: region.total_visits / region.unique_visits,
            // avg_tos: this.ssTommss(region.avg_tos),
          };
        })
      : [];
    this.selectedRegions = this.availableRegions;
  }

  initAvailableComparisonGroups() {
    this.availableComparisonGroups = this.comparisonGroupsDTO?.map(
      (regionGroup: any) => {
        const geofenceRegions: RegionDTO[] = [];
        const beaconRegions: RegionDTO[] = [];
        regionGroup.geofences_id?.forEach((geofence_id: number) => {
          const geofence = this.availableRegions.find((region) => {
            return region.id === geofence_id && region.type === "geofence";
          });
          if (geofence) {
            geofenceRegions.push(geofence);
          }
        });
        regionGroup.beacon_regions_id?.forEach((beacon_region_id: number) => {
          const beaconRegion = this.availableRegions.find((region) => {
            return (
              region.id === beacon_region_id && region.type === "beacon_region"
            );
          });
          if (beaconRegion) {
            beaconRegions.push(beaconRegion);
          }
        });

        return {
          name: regionGroup.name,
          regions: [...beaconRegions, ...geofenceRegions],
          id: regionGroup.id,
        };
      },
    );
  }

  onCreateNewPalette(region?: any) {
    this.showCreatePalette = true;
    if (region) {
      region.createNewPallateSelected = true;
    }
  }

  onCreateNewPaletteAtEnd() {
    this.showCreatePaletteAtEnd = true;
  }

  onCloseNewPalette() {
    this.showCreatePalette = false;
    this.showCreatePaletteAtEnd = false;
    this.comparisonGroup.regions.forEach((region: any) => {
      region.createNewPallateSelected = false;
    });

    this.selectedRegions.forEach((region: any) => {
      region.createNewPallateSelected = false;
    });
  }

  onSwapRegion(region: any) {
    region.swapPanelOpen = true;
  }

  onSwapRegionSelected(region: any) {
    if (this.compareRegionMode) {
      const regionIndex = this.comparisonGroup.regions.findIndex(
        (comparisonRegion: any) => {
          return (
            comparisonRegion.id === region.id &&
            comparisonRegion.type === region.type
          );
        },
      );

      this.comparisonGroup.regions[regionIndex] =
        this.paletteForm.controls.swapPallete.value;
    } else {
      const regionIndex = this.selectedRegions.findIndex(
        (selectedRegion: any) => {
          return (
            selectedRegion.id === region.id &&
            selectedRegion.type === region.type
          );
        },
      );

      this.selectedRegions[regionIndex] =
        this.paletteForm.controls.swapPallete.value;
    }

    this.paletteForm.controls.swapPallete.setValue(null, { emitEvent: false });

    region.swapPanelOpen = false;
  }

  onCompareRegions(region: any) {
    this.comparisonGroup = {
      regions: [region],
    };
  }

  closePalette(region: any) {
    if (this.compareRegionMode) {
      this.comparisonGroup.regions = this.comparisonGroup.regions.filter(
        (compareRegion: any) => {
          return !(
            compareRegion.id === region.id && compareRegion.type === region.type
          );
        },
      );
    } else {
      this.selectedRegions = this.selectedRegions.filter((selectedRegion) => {
        return !(
          selectedRegion.id === region.id && selectedRegion.type === region.type
        );
      });
    }
  }

  onAddPalleteChange(region?: any) {
    if (this.compareRegionMode) {
      if (region) {
        const index =
          this.comparisonGroup.regions.findIndex(
            (comparisonGroupRegion: any) => comparisonGroupRegion === region,
          ) ?? this.comparisonGroup.regions.length;

        this.comparisonGroup.regions.splice(
          index,
          0,
          this.paletteForm.controls.addPallate.value,
        );
      } else {
        this.comparisonGroup.regions.push(
          this.paletteForm.controls.addPallate.value,
        );
      }
    } else {
      if (region) {
        const index =
          this.selectedRegions.findIndex(
            (selectedRegionsRegion) => selectedRegionsRegion === region,
          ) ?? this.selectedRegions.length - 1;

        this.selectedRegions.splice(
          index + 1,
          0,
          this.paletteForm.controls.addPallate.value,
        );
      } else {
        this.selectedRegions.push(this.paletteForm.controls.addPallate.value);
      }
    }
    this.paletteForm.controls.addPallate.setValue(null, { emitEvent: false });
    this.showCreatePalette = false;
    this.showCreatePaletteAtEnd = false;

    if (region) {
      region.createNewPallateSelected = false;
    }
  }

  cancelComparisonGroup() {
    this.showCreatePalette = false;
    this.showCreatePaletteAtEnd = false;
    this.regionsForm.controls.comparisonGroup.setValue(null);
  }

  saveComparisonGroup() {
    if (!this.comparisonGroup.name) {
      const comparisonNameFormControl = new FormControl("", [
        Validators.required,
      ]);
      this.Dialog.open(QuestionAnswerTextComponent, {
        data: {
          questionAnswer: {
            question: "Comparison Group Name",
            formControl: comparisonNameFormControl,
          },
        },
      })
        .afterClosed()
        .pipe(takeUntil(this.onComponentDestroy))
        .subscribe((result) => {
          if (result) {
            this.uiService
              .waitForEvent(
                this.AppService.createRegionCompareGroup({
                  name: comparisonNameFormControl.value,
                  account_app_id: this.SessionService.currentAppValue!.id,
                  user_id: this.SessionService.currentUserValue!.id,
                  geofences_id: this.comparisonGroup.regions
                    .filter((region: any) => region.type === "geofence")
                    ?.map((region: any) => region.id),
                  beacon_regions_id: this.comparisonGroup.regions
                    .filter((region: any) => region.type === "beacon_region")
                    ?.map((region: any) => region.id),
                }),
              )
              .pipe(takeUntil(this.onComponentDestroy))
              .subscribe(
                (region: any) => {
                  this.uiService
                    .waitForEvent(
                      this.AppService.getRegionCompareGroups(
                        this.SessionService.currentUserValue!.id,
                        this.SessionService.currentAppValue!.id,
                      ),
                    )
                    .pipe(takeUntil(this.onComponentDestroy))
                    .subscribe((comparisonGroups) => {
                      this.comparisonGroupsDTO = comparisonGroups;
                      this.initAvailableComparisonGroups();

                      const comparisonGroup =
                        this.availableComparisonGroups.find(
                          (group: any) => group.id === region.inserted.id,
                        );
                      this.regionsForm.controls.comparisonGroup.setValue(
                        comparisonGroup,
                      );
                    });
                },
                () => {
                  this.NotificationService.openErrorSnackBar(
                    "Error creating compare group",
                  );
                },
              );
          }
        });
    } else {
      this.uiService
        .waitForEvent(
          this.AppService.updateRegionCompareGroup({
            id: this.comparisonGroup.id,
            name: this.comparisonGroup.name,
            account_app_id: this.SessionService.currentAppValue!.id,
            user_id: this.SessionService.currentUserValue!.id,
            geofences_id: this.comparisonGroup.regions
              .filter((region: any) => region.type === "geofence")
              ?.map((region: any) => region.id),
            beacon_regions_id: this.comparisonGroup.regions
              .filter((region: any) => region.type === "beacon_region")
              ?.map((region: any) => region.id),
          }),
        )
        .pipe(takeUntil(this.onComponentDestroy))
        .subscribe(
          (region: any) => {
            this.uiService
              .waitForEvent(
                this.AppService.getRegionCompareGroups(
                  this.SessionService.currentUserValue!.id,
                  this.SessionService.currentAppValue!.id,
                ),
              )
              .pipe(takeUntil(this.onComponentDestroy))
              .subscribe((comparisonGroups) => {
                this.comparisonGroupsDTO = comparisonGroups;
                this.initAvailableComparisonGroups();

                const comparisonGroup = this.availableComparisonGroups.find(
                  (group: any) => group.id === region.id,
                );
                this.regionsForm.controls.comparisonGroup.setValue(
                  comparisonGroup,
                );
              });
          },
          () => {
            this.NotificationService.openErrorSnackBar(
              "Error updating compare group",
            );
          },
        );
    }
  }

  deleteComparisonGroup() {
    this.Dialog.open(AreYouSureDialogComponent, {
      data: {
        areYouSureInfo: {
          description: `Are you sure you want to delete this comparison group ( ${this.comparisonGroup.name} )?`,
          warning: "Warning: this is not reversible!",
        },
      },
    })
      .afterClosed()
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((res) => {
        if (res) {
          this.uiService
            .waitForEvent(
              this.AppService.deleteRegionCompareGroup(this.comparisonGroup.id),
            )
            .pipe(takeUntil(this.onComponentDestroy))
            .subscribe(
              () => {
                this.uiService
                  .waitForEvent(
                    this.AppService.getRegionCompareGroups(
                      this.SessionService.currentUserValue!.id,
                      this.SessionService.currentAppValue!.id,
                    ),
                  )
                  .pipe(takeUntil(this.onComponentDestroy))
                  .subscribe((comparisonGroups) => {
                    this.comparisonGroupsDTO = comparisonGroups;
                    this.initAvailableComparisonGroups();
                    this.comparisonGroup = null;
                  });
              },
              () => {
                this.NotificationService.openErrorSnackBar(
                  "Error deleting compare group",
                );
              },
            );
        }
      });
  }

  applyFilter() {
    this.selectedRegions = this.availableRegions.filter((region) =>
      region.name.toLowerCase().includes(this.searchValue.value.toLowerCase()),
    );
    this.initTable();
  }

  exportToExcel() {
    let compareGroup = (this.comparisonGroup != null ? this.comparisonGroup.id : null);
    this.uiService
      .waitForEvent(
        this.APIService.get(
          `account-apps-device/${this.SessionService.currentAccountValue!.accountID}/app_device/regions?start=${this.filterForm.controls.startDate.value.toISOString()}&end=${this.filterForm.controls.endDate.value.toISOString()}&forExport=true&comparisonGroup=${compareGroup}`,
          {
            'responseType': 'blob' as 'xlsx'
          },
        )
      )
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(
        (res) => {
          this.fileSaver.save(
            <any>res,
            moment().format(
              "[LHPERegions-]YYYY-MM-DD-HH-mm-ss[.xlsx]",
            ),
          );
        },
        (error) => {
          console.error("download error:", error);
        },
        () => {
          console.log('Completed file download.')
        },
      );
  }

  onPageChange(e: PageEvent){
    this.localStorageSvc.pageSize = e.pageSize;
    this.perPage = e.pageSize;
  }

  // ssTommss(secs: string) {
  //   const secsint = Number.parseInt(secs);
  //   let minutes = "";
  //   let seconds = "";

  //   const minuteInt = Math.floor(secsint / 60);
  //   const secondsInt = secsint % 60;
  //   minutes = minuteInt > 9 ? `${minuteInt}` : `0${minuteInt}`;
  //   seconds = secondsInt > 9 ? `${secondsInt}` : `0${secondsInt}`;

  //   return `${minutes}:${seconds}`;
  // }
}
